import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchGetIrregularityByIdService,
  fetchGetPayableIrregularitiesByPlateService,
  fetchGetUnpayableIrregularitiesByPlateService,
  fetchGetIrregularityListByIdsService,
  fetchRegularizeService,
  fetchRegularizeListService,
  fetchAITdataService,
  fetchAnyIrregularityService,
} from '../../services';
import 'react-toastify/dist/ReactToastify.css';
import { IrregularitiesStateType } from '../types';
import { map } from 'lodash';
import moment from 'moment';

const initialState: IrregularitiesStateType = {
  irregularitiesLoading: false,
  irregularityDetails: null,
  irregularityPayablesListText: null,
  irregularityPayablesListNewText: null,
  irregularityUnpayablesListText: null,
  irregularityUnpayablesListNewText: null,
  irregularityPayablesList: [],
  irregularityUnpayablesList: [],
  irregularityListDetails: null,
  showDetails: false,
  showListDetails: false,
  searchIsOpen: false,
  searchingByCode: true,
  searchedPlate: null,
  regularizeIsOpen: false,
  regularizeListIsOpen: false,
  createVehicleForIrregularityIsOpen: false,
  dashboardNewIrregularityPolicyIsOpen: true,
  newIrregularityPolicyIsOpen: true,
  paymentMethod: null,
  goBackToList: false,
  editCPFIsOpen: false,
  editCPFLoading: false,
  selectedIds: [],
  allIdsSelected: false,
  allOldIdsSelected: false,
  allNewIdsSelected: false,
  AITdata: null,
   hasAnyIrregularity: false,
};

export const fetchGetIrregularityById = createAsyncThunk(
  'getIrregularityById',
  async (params: { driverId: string; irregularityId: string }) => {
    return await fetchGetIrregularityByIdService(params);
  },
);

export const fetchGetIrregularitiesByPlate = createAsyncThunk(
  'getIrregularitiesByPlate',
  async (params: { driverId: string; plate: string }) => {
    const payables = await fetchGetPayableIrregularitiesByPlateService(params);
    const unpayables = await fetchGetUnpayableIrregularitiesByPlateService(
      params,
    );

    return {
      payables: payables,
      unpayables: unpayables,
    };
  },
);

export const fetchGetIrregularityListByIds = createAsyncThunk(
  'getIrregularityListByIds',
  async (params: { driverId: string; irregularityListIds: string[] }) => {
    return await fetchGetIrregularityListByIdsService(params);
  },
);

export const fetchRegularize = createAsyncThunk(
  'regularize',
  async (params: {
    irregularityId: string;
    driverId: string;
    plate: string;
    type: string;
    entryDateTime: string;
    exitDateTime: string;
    stayTime: string;
    price: number;
    routeId: number;
    vehicleId: number | null;
    paymentMethodId: number;
    ipAddress: string;
  }) => {
    return await fetchRegularizeService(params);
  },
);

export const fetchRegularizeList = createAsyncThunk(
  'regularizeList',
  async (params: {
    driverId: string;
    plate: string;
    type: string;
    entryDateTime: string;
    exitDateTime: string;
    stayTime: string;
    price: number;
    routeId: number;
    vehicleId: number | null;
    irregularities: number[];
    paymentMethodId: number;
    observation: string;
    ipAddress: string;
  }) => {
    return await fetchRegularizeListService(params);
  },
);

export const fetchAITdata = createAsyncThunk('AITdata', async () => {
  return await fetchAITdataService();
});

export const fetchAnyIrregularity = createAsyncThunk(
  'anyIrregularity',
  async (params: { driverId: string }) => {
    return await fetchAnyIrregularityService(params);
  },
);

export const irregularitiesSlice = createSlice({
  name: 'irregularities',
  initialState,
  reducers: {
    toggleIrregularitiesLoading(state) {
      state.irregularitiesLoading = !state.irregularitiesLoading;
    },
    toggleShowDetails(state) {
      state.showDetails = !state.showDetails;
    },
    toggleShowListDetails(state) {
      state.showListDetails = !state.showListDetails;
    },
    /** Volta à lista/busca principal (ex.: ao abrir Irregularidades pelo menu lateral). */
    resetToIrregularitiesMainView(state) {
      state.showDetails = false;
      state.showListDetails = false;
      state.irregularityDetails = null;
      state.irregularityListDetails = null;
      state.selectedIds = [];
      state.allIdsSelected = false;
    },
    setGoBackToList(state) {
      state.goBackToList = !state.goBackToList;
      state.selectedIds = [];
    },
    toggleSearchIsOpen(state) {
      return {
        ...state,
        searchIsOpen: !state.searchIsOpen,
      };
    },
    toggleSearchingByCode(state) {
      return {
        ...state,
        searchingByCode: !state.searchingByCode,
      };
    },
    toggleRegularizeIsOpen(state) {
      return {
        ...state,
        regularizeIsOpen: !state.regularizeIsOpen,
      };
    },
    toggleRegularizeListIsOpen(state) {
      return {
        ...state,
        regularizeListIsOpen: !state.regularizeListIsOpen,
      };
    },
    toggleCreateVehicleForIrregularityIsOpen(state) {
      return {
        ...state,
        createVehicleForIrregularityIsOpen:
          !state.createVehicleForIrregularityIsOpen,
      };
    },
    toggleDashboardNewIrregularityPolicyIsOpen(state) {
      return {
        ...state,
        dashboardNewIrregularityPolicyIsOpen:
          !state.dashboardNewIrregularityPolicyIsOpen,
      };
    },
    toggleNewIrregularityPolicyIsOpen(state) {
      return {
        ...state,
        newIrregularityPolicyIsOpen: !state.newIrregularityPolicyIsOpen,
      };
    },
    setPaymentMethod(
      state,
      action: PayloadAction<
        | 'PIX'
        | {
            id: number;
            number: string;
            brand: string;
            bankId: number | null;
            default: 0 | 1;
          }
        | null
      >,
    ) {
      state.paymentMethod = action.payload;
    },
    toggleEditCPFIrregularitiesIsOpen(state) {
      return {
        ...state,
        editCPFIsOpen: !state.editCPFIsOpen,
      };
    },
    toggleEditCPFIrregularitiesLoading(state) {
      return {
        ...state,
        editCPFLoading: !state.editCPFLoading,
      };
    },
    setSelectedIds(state, action: PayloadAction<number[]>) {
      state.selectedIds = action.payload;
    },
    resetIrregularityListDetails(state) {
      return {
        ...state,
        selectedIds: [],
        irregularityListDetails: null,
      };
    },
    resetSearchedPlate(state) {
      return {
        ...state,
        irregularityList: [],
        selectedIds: [],
        searchedPlate: null,
      };
    },
    resetIrregularitiesList(state) {
      return {
        ...state,
        irregularityList: [],
      };
    },
    setAllIdsSelected(state, action: PayloadAction<boolean>) {
      state.allIdsSelected = action.payload;
    },
    setAllOldIdsSelected(state, action: PayloadAction<boolean>) {
      state.allOldIdsSelected = action.payload;
    },
    setAllNewIdsSelected(state, action: PayloadAction<boolean>) {
      state.allNewIdsSelected = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGetIrregularityById.pending, (state) => {
      return {
        ...state,
        irregularitiesLoading: true,
        irregularityDetails: null,
      };
    }),
      builder.addCase(fetchGetIrregularityById.fulfilled, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
          irregularityDetails: res.payload.data,
          selectedIds: [],
          allIdsSelected: false,
        };
      }),
      builder.addCase(fetchGetIrregularityById.rejected, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
        };
      });

    builder.addCase(fetchGetIrregularitiesByPlate.pending, (state) => {
      return {
        ...state,
        irregularitiesLoading: true,
        irregularityPayablesList: [],
        irregularityUnpayablesList: [],
        selectedIds: [],
      };
    }),
      builder.addCase(fetchGetIrregularitiesByPlate.fulfilled, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
          irregularityPayablesListText: res.payload.payables?.data?.text || '',
          irregularityPayablesListNewText:
            res.payload.payables?.data?.newText || '',
          irregularityUnpayablesListText:
            res.payload.unpayables?.data?.text || '',
          irregularityUnpayablesListNewText:
            res.payload.unpayables?.data?.newText || '',
          irregularityPayablesList: res.payload.payables?.data?.list || [],
          irregularityUnpayablesList: res.payload.unpayables?.data?.list || [],
          searchedPlate: res.meta.arg.plate,
          selectedIds: [],
          allIdsSelected: false,
        };
      }),
      builder.addCase(fetchGetIrregularitiesByPlate.rejected, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
          searchedPlate: res.meta.arg.plate,
        };
      });

    builder.addCase(fetchGetIrregularityListByIds.pending, (state) => {
      return {
        ...state,
        irregularitiesLoading: true,
        irregularityListDetails: null,
      };
    }),
      builder.addCase(fetchGetIrregularityListByIds.fulfilled, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
          irregularityListDetails: res.payload.data,
          selectedIds: [],
          allIdsSelected: false,
        };
      }),
      builder.addCase(fetchGetIrregularityListByIds.rejected, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
        };
      });

    builder.addCase(fetchRegularize.pending, (state) => {
      return {
        ...state,
        irregularitiesLoading: true,
      };
    }),
      builder.addCase(fetchRegularize.fulfilled, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
        };
      }),
      builder.addCase(fetchRegularize.rejected, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
        };
      });

    builder.addCase(fetchRegularizeList.pending, (state) => {
      return {
        ...state,
        irregularitiesLoading: true,
      };
    }),
      builder.addCase(fetchRegularizeList.fulfilled, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
        };
      }),
      builder.addCase(fetchRegularizeList.rejected, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
        };
      });

    builder.addCase(fetchAITdata.pending, (state) => {
      return {
        ...state,
        irregularitiesLoading: true,
      };
    }),
      builder.addCase(fetchAITdata.fulfilled, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
          AITdata: res.payload.data[0],
        };
      }),
      builder.addCase(fetchAITdata.rejected, (state, res) => {
        return {
          ...state,
          irregularitiesLoading: false,
        };
      });

    builder.addCase(fetchAnyIrregularity.fulfilled, (state, res) => {
      return {
        ...state,
        hasAnyIrregularity: res.payload.data.anyIrregularity,
      };
    });
  },
});

// Action creators are generated for each case reducer function
export const {
  toggleIrregularitiesLoading,
  toggleShowDetails,
  resetToIrregularitiesMainView,
  setGoBackToList,
  setPaymentMethod,
  toggleSearchIsOpen,
  toggleSearchingByCode,
  toggleRegularizeIsOpen,
  toggleRegularizeListIsOpen,
  toggleEditCPFIrregularitiesIsOpen,
  toggleEditCPFIrregularitiesLoading,
  setSelectedIds,
  resetIrregularityListDetails,
  resetSearchedPlate,
  resetIrregularitiesList,
  setAllIdsSelected,
} = irregularitiesSlice.actions;
